import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PageHeading, ViewTabs, Th, NtIcon } from '../components/NotionUI';
import {
  getProjects,
  createProject,
  createTicketProject,
  linkTicketProject,
  deleteProject,
  restoreProject,
  archiveProject,
  setProjectFavorite,
  reorderProjects,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { setProjectMenu } from '../projectMenu';
import { canAction } from '../menu';
import { fmtDateDMY } from '../utils/format';

const TABS = ['active', 'archived', 'trash'];
const VIEW_KEY = 'ticket.projectsView';

const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards'; } catch (e) { return 'cards'; }
};

// Sfumatura della copertina: quella scelta nel progetto, altrimenti derivata dal suo colore.
const coverStyle = (project) => (project.cover ? undefined : {
  background: `linear-gradient(120deg, color-mix(in srgb, ${project.color || '#8a8f98'} 55%, #fff), color-mix(in srgb, ${project.color || '#8a8f98'} 22%, #fff))`,
});

function Projects({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [state, setState] = useState('active');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [menu, setMenu] = useState(null); // {project, top, left}
  const [dragId, setDragId] = useState(null);
  const [dropMark, setDropMark] = useState(null);
  const [view, setView] = useState(readView);

  const changeView = (next) => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch (e) { /* preferenza non salvata */ }
  };

  // La sidebar mostra sempre i progetti attivi, qualunque scheda sia aperta.
  const refreshSidebar = useCallback(async () => {
    setProjectMenu((await getProjects('active')).projects || []);
  }, []);

  const load = useCallback(async (which) => {
    setLoading(true);
    setError('');
    try {
      const data = await getProjects(which);
      setProjects(data.projects || []);
      if (which === 'active') setProjectMenu(data.projects || []);
    } catch (err) {
      setError(errorMessage(err, t('projects.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(state);
  }, [state, load]);

  // "Nuovo progetto" chiede il nome e se creare anche una coda di assistenza con lo stesso nome (due cose distinte, non collegate).
  const [newName, setNewName] = useState(null); // null = finestra chiusa
  const canCreateQueue = canAction(user, 'ticket_projects.manage');
  const handleCreate = () => setNewName('');

  const createNew = async (alsoQueue) => {
    const name = newName.trim() || t('projects.untitledProject');
    setNewName(null);
    setCreating(true);
    setError('');
    try {
      const data = await createProject({ name, lang });
      if (alsoQueue) {
        try {
          const queue = await createTicketProject({ name });
          await linkTicketProject(queue.project.id, data.project.id);
        } catch (err) {
          setError(t('projects.queueError', { error: errorMessage(err) }));
        }
      }
      await refreshSidebar();
      navigate(`/projects/${data.project.id}`);
    } catch (err) {
      setError(errorMessage(err, t('projects.saveError')));
      setCreating(false);
    }
  };

  // Nuovo progetto da un file CSV: si crea con il nome del file e si apre l'importazione.
  const handleImportCsv = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv,text/plain';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      setCreating(true);
      try {
        const text = await file.text();
        const data = await createProject({ name: file.name.replace(/\.[^.]+$/, '') || t('projects.untitledProject'), lang });
        await refreshSidebar();
        navigate(`/projects/${data.project.id}`, { state: { importCsv: text, importName: file.name } });
      } catch (err) {
        setError(errorMessage(err, t('projects.saveError')));
        setCreating(false);
      }
    };
    input.click();
  };

  const toggleFavorite = async (project) => {
    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, is_favorite: !p.is_favorite } : p)));
    try {
      await setProjectFavorite(project.id, !project.is_favorite);
      await refreshSidebar();
    } catch (err) {
      setError(errorMessage(err, t('projects.saveError')));
      load(state);
    }
  };

  const run = async (fn, errorKey) => {
    try {
      await fn();
      await load(state);
      await refreshSidebar();
    } catch (err) {
      setError(errorMessage(err, t(errorKey)));
    }
  };

  const drop = async (target) => {
    const source = dragId;
    setDragId(null);
    setDropMark(null);
    if (source === null || source === target.id) return;
    const ids = projects.map((p) => p.id).filter((id) => id !== source);
    const at = ids.indexOf(target.id);
    ids.splice(dropMark && dropMark.where === 'after' ? at + 1 : at, 0, source);
    setProjects(ids.map((id) => projects.find((p) => p.id === id)));
    try {
      await reorderProjects(ids);
      await refreshSidebar();
    } catch (err) {
      setError(errorMessage(err, t('projects.saveError')));
      load(state);
    }
  };

  const rowActions = (project) => {
    if (state === 'active') {
      return [
        ['archive', 'folder', t('projects.archive'), () => run(() => archiveProject(project.id, true), 'projects.saveError')],
        ['trash', 'trash', t('projects.moveToTrash'), () => run(() => deleteProject(project.id), 'projects.saveError')],
      ];
    }
    if (state === 'archived') {
      return [
        ['unarchive', 'folder', t('projects.unarchive'), () => run(() => archiveProject(project.id, false), 'projects.saveError')],
        ['trash', 'trash', t('projects.moveToTrash'), () => run(() => deleteProject(project.id), 'projects.saveError')],
      ];
    }
    return [
      ['restore', 'folder', t('projects.restore'), () => run(() => restoreProject(project.id), 'projects.saveError')],
      ['forever', 'trash', t('projects.deleteForever'), () => window.confirm(t('projects.deleteForeverConfirm', { name: project.name })) && run(() => deleteProject(project.id, true), 'projects.saveError')],
    ];
  };

  const openMenu = (e, project) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ project, top: rect.bottom + 2, left: Math.max(8, rect.left - 180) });
  };

  const dragProps = (p, horizontal) => ({
    onDragOver: (e) => {
      if (dragId === null || dragId === p.id) return;
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const before = horizontal ? e.clientX < rect.left + rect.width / 2 : e.clientY < rect.top + rect.height / 2;
      setDropMark({ id: p.id, where: before ? 'before' : 'after' });
    },
    onDrop: (e) => { e.preventDefault(); drop(p); },
  });

  const progressOf = (p) => (p.task_count > 0 ? Math.round((p.done_count / p.task_count) * 100) : 0);

  const renderCard = (p) => {
    const percent = progressOf(p);
    return (
      <div
        key={p.id}
        role="link"
        tabIndex={0}
        className={'pj-card' + (dragId === p.id ? ' pj-dragging' : '') + (dropMark && dropMark.id === p.id ? ` pj-drop-${dropMark.where}` : '')}
        draggable={state === 'active'}
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(p.id)); setDragId(p.id); }}
        onDragEnd={() => { setDragId(null); setDropMark(null); }}
        onClick={() => state !== 'trash' && navigate(`/projects/${p.id}`)}
        onKeyDown={(e) => e.key === 'Enter' && state !== 'trash' && navigate(`/projects/${p.id}`)}
        {...dragProps(p, true)}
      >
        <div className={'pj-cover' + (p.cover ? ` pd-cover-${p.cover}` : '')} style={coverStyle(p)}>
          <div className="pj-actions">
            {state !== 'trash' && (
              <button
                type="button"
                className={'pj-chip pj-star' + (p.is_favorite ? ' pj-star-on' : '')}
                aria-label={p.is_favorite ? t('projects.removeFavorite') : t('projects.addFavorite')}
                title={p.is_favorite ? t('projects.removeFavorite') : t('projects.addFavorite')}
                onClick={(e) => { e.stopPropagation(); toggleFavorite(p); }}
              >
                {p.is_favorite ? '★' : '☆'}
              </button>
            )}
            <button type="button" className="pj-chip" aria-label={t('projectDetail.rowActions')} onClick={(e) => openMenu(e, p)}>
              <NtIcon name="more" />
            </button>
          </div>
        </div>
        <span className="pj-icon" aria-hidden="true">{p.icon || '📋'}</span>
        <div className="pj-body">
          <div className="pj-name">{p.name}</div>
          <div className="pj-meta">
            {t('projects.tasksLabel', { count: p.task_count })}
            <span aria-hidden="true"> · </span>
            {fmtDateDMY(state === 'trash' ? p.deleted_at : p.created_at)}
          </div>
          {p.progress_tracked && p.task_count > 0 ? (
            <div className="pj-progress" title={`${p.done_count}/${p.task_count}`}>
              <span className="pj-track"><span className="pj-fill" style={{ width: `${percent}%`, background: p.color }} /></span>
              <span className="pj-percent">{percent}%</span>
            </div>
          ) : (
            <div className="pj-progress pj-progress-empty" aria-hidden="true" />
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="briefcase" title={t('projects.title')} subtitle={t('projects.subtitle')} />

        <ViewTabs
          tabs={[
            { key: 'active', icon: 'star', label: t('projects.allTab') },
            { key: 'archived', icon: 'folder', label: t('projects.archivedTab') },
            { key: 'trash', icon: 'trash', label: t('projects.trashTab') },
          ]}
          active={state}
          onChange={(key) => TABS.includes(key) && setState(key)}
        >
          <span className="pj-view-toggle" role="group" aria-label={t('projects.viewMode')}>
            <button type="button" className={'pj-view-btn' + (view === 'cards' ? ' pj-view-active' : '')} aria-pressed={view === 'cards'} title={t('projects.viewCards')} aria-label={t('projects.viewCards')} onClick={() => changeView('cards')}>
              <NtIcon name="layout" />
            </button>
            <button type="button" className={'pj-view-btn' + (view === 'list' ? ' pj-view-active' : '')} aria-pressed={view === 'list'} title={t('projects.viewList')} aria-label={t('projects.viewList')} onClick={() => changeView('list')}>
              <NtIcon name="list" />
            </button>
          </span>
          <button className="nt-ghost-btn" type="button" onClick={() => window.dispatchEvent(new Event('open-global-search'))}>
            <NtIcon name="search" /> {t('projects.search')}
          </button>
          <button className="nt-ghost-btn" type="button" onClick={handleImportCsv} disabled={creating}>
            {t('projects.importCsv')}
          </button>
          <button className="nt-ghost-btn" type="button" onClick={() => navigate('/project-templates')}>
            {t('projects.fromTemplate')}
          </button>
          <button className="btn" type="button" onClick={handleCreate} disabled={creating}>
            {t('projects.newProject')}
          </button>
        </ViewTabs>

        {error && <div className="error-message">{error}</div>}
        {newName !== null && (
          <>
            <div className="pd-modal-overlay" onClick={() => setNewName(null)} />
            <div className="pd-modal" role="dialog" aria-label={t('projects.newProject')}>
              <h3 className="pd-modal-title">{t('projects.newProject')}</h3>
              <div className="pd-modal-field">
                <label>{t('projects.newDialogName')}</label>
                <input
                  className="pd-input"
                  autoFocus
                  value={newName}
                  placeholder={t('projects.untitledProject')}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createNew(false)}
                />
              </div>
              {canCreateQueue && <p className="nt-muted">{t('projects.newDialogQueueHint')}</p>}
              <div className="pd-popover-actions">
                <button type="button" className="btn" onClick={() => createNew(false)}>{canCreateQueue ? t('projects.createProjectOnly') : t('projects.newProject')}</button>
                {canCreateQueue && <button type="button" className="btn btn-secondary" onClick={() => createNew(true)}>{t('projects.createProjectAndQueue')}</button>}
                <button type="button" className="btn btn-secondary" onClick={() => setNewName(null)}>{t('common.cancel')}</button>
              </div>
            </div>
          </>
        )}
        {state === 'trash' && <p className="nt-muted">{t('projects.trashHint', { days: 30 })}</p>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : view === 'cards' ? (
          projects.length === 0 ? (
            <p className="nt-empty">{t(`projects.empty_${state}`)}</p>
          ) : (
            <div className="pj-grid">
              {projects.map(renderCard)}
              {state === 'active' && (
                <button type="button" className="pj-card pj-card-new" onClick={handleCreate} disabled={creating}>
                  <span className="pj-new-plus" aria-hidden="true">+</span>
                  {t('projects.newProject')}
                </button>
              )}
            </div>
          )
        ) : (
          <div className="nt-table-wrap">
            {projects.length === 0 ? (
              <p className="nt-empty">{t(`projects.empty_${state}`)}</p>
            ) : (
              <table className="nt-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <Th icon="text">{t('projects.name')}</Th>
                    <Th icon="hash">{t('projects.tasks')}</Th>
                    <Th icon="status">{t('projects.progress')}</Th>
                    <Th icon="calendar">{t('projects.created')}</Th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => {
                    const percent = progressOf(p);
                    return (
                      <tr
                        key={p.id}
                        className={'nt-clickable' + (dropMark && dropMark.id === p.id ? ` pd-drop-${dropMark.where}` : '')}
                        onClick={() => state !== 'trash' && navigate(`/projects/${p.id}`)}
                        {...dragProps(p, false)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          {state === 'active' ? (
                            <span className="pd-grip pd-grip-visible" draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(p.id)); setDragId(p.id); }} onDragEnd={() => { setDragId(null); setDropMark(null); }} title={t('projects.dragToReorder')}>
                              <NtIcon name="grip" />
                            </span>
                          ) : null}
                        </td>
                        <td className="nt-cell-title">
                          {state !== 'trash' && (
                            <button
                              type="button"
                              className={'pd-star' + (p.is_favorite ? ' pd-star-on' : '')}
                              aria-label={p.is_favorite ? t('projects.removeFavorite') : t('projects.addFavorite')}
                              title={p.is_favorite ? t('projects.removeFavorite') : t('projects.addFavorite')}
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(p); }}
                            >
                              {p.is_favorite ? '★' : '☆'}
                            </button>
                          )}
                          <span className="pd-name-icon" aria-hidden="true">{p.icon || '📋'}</span> {p.name}
                        </td>
                        <td>{p.task_count}</td>
                        <td>
                          {p.progress_tracked && p.task_count > 0 ? (
                            <span className="pd-bar" title={`${p.done_count}/${p.task_count}`}>
                              <span className="pd-bar-fill" style={{ width: `${percent}%`, background: p.color }} />
                              <span className="pd-bar-label">{percent}%</span>
                            </span>
                          ) : (
                            <span className="nt-muted">—</span>
                          )}
                        </td>
                        <td>{fmtDateDMY(state === 'trash' ? p.deleted_at : p.created_at)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="pd-row-delete"
                            style={{ opacity: 1 }}
                            aria-label={t('projectDetail.rowActions')}
                            onClick={(e) => openMenu(e, p)}
                          >
                            <NtIcon name="more" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {state === 'active' && (
              <button type="button" className="nt-add-row" onClick={handleCreate} disabled={creating}>
                <span aria-hidden="true">+</span> {t('projects.newProject')}
              </button>
            )}
          </div>
        )}

        {menu && (
          <>
            <div className="pd-overlay" onClick={() => setMenu(null)} />
            <div className="pd-popover pv-menu" style={{ position: 'fixed', top: menu.top, left: menu.left }}>
              {rowActions(menu.project).map(([key, icon, label, action]) => (
                <button key={key} type="button" className="pv-menu-item" onClick={() => { setMenu(null); action(); }}>
                  <NtIcon name={icon} /> {label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default Projects;
